/**
 * AuthContext.tsx — the signed-in session, shared across the app.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: auth is app-wide state — the dashboard, checkout account step and role
 * gates all need to know who is signed in. On mount, if a token exists we hydrate
 * `me` from GET /auth/me. login/register persist the returned opaque token and
 * seed `me`. A 401 anywhere (via the client) clears the session and routes to
 * /auth. The UI gate is presentation only (R41) — the gateway is the real
 * control; a hand-edited URL never yields data.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { login as apiLogin, register as apiRegister, getMe } from '../api/identity';
import type { LoginRequest, Me, RegisterRequest } from '../api/types';
import { clearToken, getToken, onUnauthorized, setToken } from './session';

interface AuthContextValue {
  me: Me | null;
  status: 'loading' | 'ready';
  isSignedIn: boolean;
  login: (body: LoginRequest) => Promise<Me>;
  register: (body: RegisterRequest) => Promise<Me>;
  /**
   * Sign out and go to `/auth`. `next` (a basename-relative path + search, as
   * the router reports it) makes that the SIGN-IN screen with `?next=` — the
   * same shape the 401 handler below builds, honoured by AuthPage through
   * `safeNextForRole` — for a screen whose way on is "sign in as somebody
   * else, then come back here" (the checkout's "different email address" hold,
   * 2026-09-26). Without it, exactly what sign-out always did.
   */
  logout: (options?: { next?: string }) => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Provide the auth session to the tree. Mount inside the router. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  // The 401 listener below is registered ONCE; it reads the CURRENT location
  // through a ref so it never builds `?next=` from a stale render.
  const locationRef = useRef(location);
  locationRef.current = location;
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setStatus('ready');
      return;
    }
    try {
      setMe(await getMe());
    } catch {
      // Invalid/expired token — the client already cleared it on a 401.
      setMe(null);
    } finally {
      setStatus('ready');
    }
  }, []);

  // Hydrate the session once on mount.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A 401 anywhere clears local state and routes to sign-in.
  useEffect(() => {
    return onUnauthorized(() => {
      setMe(null);
      // Session expired mid-use (any API 401): prompt a re-login and come back.
      // `next` is built from the ROUTER's location (basename-relative), never
      // window.location: under a served prefix ('/clinics', Clinics-in-Core) the
      // raw pathname would carry the prefix twice after the redirect AND would
      // slip past roleHome's '/admin' / '/clinicians' prefix guards. Same source
      // of truth as RequireRole, so the two can never disagree.
      const { pathname, search } = locationRef.current;
      navigate(`/auth?next=${encodeURIComponent(`${pathname}${search}`)}`, { replace: true });
    });
  }, [navigate]);

  const login = useCallback(async (body: LoginRequest) => {
    const session = await apiLogin(body);
    setToken(session.sessionToken);
    const principal: Me = {
      userId: session.userId,
      email: body.email,
      role: session.role,
      emailVerified: session.emailVerified,
    };
    setMe(principal);
    setStatus('ready');
    return principal;
  }, []);

  const register = useCallback(async (body: RegisterRequest) => {
    const session = await apiRegister(body);
    setToken(session.sessionToken);
    const principal: Me = {
      userId: session.userId,
      email: body.email,
      role: session.role,
      emailVerified: session.emailVerified,
    };
    setMe(principal);
    setStatus('ready');
    return principal;
  }, []);

  const logout = useCallback(
    (options?: { next?: string }) => {
      clearToken();
      setMe(null);
      // `typeof` rather than truthiness alone: a caller that hands this straight
      // to `onClick` would pass the click event here. Only a real path counts.
      const next = typeof options?.next === 'string' && options.next.startsWith('/') ? options.next : null;
      navigate(next ? `/auth?mode=login&next=${encodeURIComponent(next)}` : '/auth', { replace: true });
    },
    [navigate],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ me, status, isSignedIn: me !== null, login, register, logout, refresh }),
    [me, status, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Read the auth session. Throws if used outside the provider (a wiring bug). */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
